import { useEffect, useState } from 'react';
import { z } from 'zod';
import styled, { css } from 'styled-components';
import {
  ButtonContainer,
  InputCheck,
  InputWrapper,
  Loading,
  PrimaryButton,
  SecondaryButton,
  SectionHeading,
  SecondaryAltButton,
  SectionWrapper
} from '../../atoms';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Request } from '../../../apis';
import { toast } from 'react-hot-toast';

type TUserCreationType = 'platform_user' | 'tenant_user';

type TEditUserAccountDetails = {
  content?: any; //CAM TODO
  userType: TUserCreationType;
  apiEndpoint: string | null;
  isModalOpen: boolean;
  refresh: {
    shouldRefresh: boolean;
    setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  };
};

type UserPermissions = {
  readOnlyAccess: boolean;
  paymentAssistAgreement: boolean;
};

// checkbox hidden till implemented, commenting out
// type EmailPreferences = {
//   enableAllNotifications: boolean;
// };

type TEditUserRequest = {
  email: string;
  mobile: string;
  name: string;
  title: string;
  role: string;
  permissions: UserPermissions;
  // emailPreferences: EmailPreferences; checkbox hidden till implemented, commenting out
};

const HeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Heading = styled((props) => <SectionHeading {...props} />)`
  margin-top: 20px !important;
  margin-bottom: 20px;
`;

const EditWrapper = styled.div(
  ({ theme }) => css`
    padding-top: ${theme.paddings.sm};
    padding-bottom: ${theme.paddings.sm};
  `
);

const ErrorStyles = styled.div(
  ({ theme }) => css`
    text-align: right;
    color: ${theme.colors.main.error};
    margin-bottom: 15px;
  `
);

const FormFooter = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 5px;
  margin-bottom: 20px;
`;

const editAccountDetailsFormSchema = z.object({
  fullName: z
    .string()
    .regex(
      /^[a-zA-Z]{1,}( {1,2}[a-zA-Z]{1,}){0,}$/,
      'Please enter a valid full name.'
    ),
  title: z.string().min(1, 'Title must be at least 1 character.'),
  readOnly: z.boolean().optional(),
  pas: z
    .string()
    .transform((val) => (val === 'true' ? true : false))
    .optional(),
  emailNotifications: z.boolean().optional()
});

type EditAccountDetailsFormSchema = z.infer<
  typeof editAccountDetailsFormSchema
>;

const EditUserAccountDetails = ({
  content,
  apiEndpoint,
  userType,
  isModalOpen,
  refresh
}: TEditUserAccountDetails) => {
  let pasString = undefined;

  if (content.pas === true) {
    pasString = 'true';
  } else if (content.pas === false) {
    pasString = 'false';
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<EditAccountDetailsFormSchema>({
    resolver: zodResolver(editAccountDetailsFormSchema),
    defaultValues: {
      ...content,
      pas: pasString
    }
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [editing, setEditing] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [contentState, setContentState] = useState(content);

  const handleReset = () => {
    setEditing(false);
    setProcessing(false);
    setErrorMessage('');
    // reset form
    reset(contentState);
  };

  useEffect(() => {
    if (isModalOpen === false && editing === true) {
      handleReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const onSubmit: SubmitHandler<EditAccountDetailsFormSchema> = async (
    data: any
  ) => {
    setErrorMessage('');
    if (!apiEndpoint) {
      setErrorMessage('Something unexpected occurred.');
      return;
    }
    // simple object check for now
    if (JSON.stringify(contentState) === JSON.stringify(data)) {
      setErrorMessage('No changes made');
      return;
    }
    try {
      const { emailNotifications, fullName, pas, readOnly, title } = data;
      const editUserRequest: Partial<TEditUserRequest> = {
        name: fullName,
        title
      };

      if (userType === 'tenant_user') {
        // checkbox hidden till implemented, commenting out
        // editUserRequest.emailPreferences = {
        //   enableAllNotifications: emailNotifications === true
        // };

        editUserRequest.permissions = {
          paymentAssistAgreement: pas,
          readOnlyAccess: false
          // checkbox hidden till implemented, commenting out
          // readOnlyAccess: readOnly === true
        };
      }

      setProcessing(true);
      const response = await Request.patch(
        `${apiEndpoint}`,
        z.any(),
        editUserRequest
      );

      if (response.status === 200) {
        setContentState(data);
        setProcessing(false);
        setEditing(false);
        toast.success('User edited.');

        if (refresh.shouldRefresh === false) {
          refresh.setShouldRefresh(true);
        }
      } else {
        setErrorMessage('Something unexpected occurred.');
        setProcessing(false);
      }
    } catch (err: any) {
      //setErrorMessage(err.message); -- replaced with generic error
      setErrorMessage('Something unexpected occurred.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate={true}>
      <SectionWrapper>
        <HeadingWrapper>
          <Heading>Account Details</Heading>
          {!editing && (
            <EditWrapper>
              <SecondaryAltButton
                type="button"
                onClick={(e) => {
                  setEditing(true);
                }}
              >
                Edit Account Details{' '}
                <span className="material-symbols-rounded">edit</span>
              </SecondaryAltButton>
            </EditWrapper>
          )}
        </HeadingWrapper>
        <InputWrapper
          name="fullName"
          type="text"
          label="Full Name"
          disabled={!editing}
          register={register}
          defaultValue={contentState.fullName}
          errorMessage={errors.fullName?.message}
        />
        <InputWrapper
          name="title"
          type="text"
          label="Title"
          disabled={!editing}
          register={register}
          defaultValue={contentState.title}
          errorMessage={errors.title?.message}
        />

        {userType === 'tenant_user' && (
          <>
            {/* hiding checkbox for now till implemented */}
            {/* <InputCheck
              name="readOnly"
              label="Read Only Access"
              required
              disabled={!editing}
              register={register}
              defaultChecked={contentState.readOnly}
            /> */}
            <InputWrapper
              name="pas"
              type="select"
              label="Payment Assist agreement"
              required
              complexOptions={[
                {
                  value: 'true',
                  text: 'Allowed to view and download'
                },
                {
                  value: 'false',
                  text: 'Not Allowed to view and download'
                }
              ]}
              disabled={!editing}
              register={register}
              defaultValue={contentState.pas.toString()}
              errorMessage={errors.pas?.message}
            />
            {/* hiding checkbox for now till implemented */}
            {/* <InputCheck
              name="emailNotifications"
              label="Receive Email Notifications"
              required
              disabled={!editing}
              register={register}
              defaultChecked={contentState.emailNotifications}
              errorMessage={errors.emailNotifications?.message}
            /> */}
          </>
        )}
        <ErrorStyles>{errorMessage}</ErrorStyles>
      </SectionWrapper>
      {editing && (
        <FormFooter
          horizontalSpacing="10px"
          verticalSpacing="0px"
          justify="end"
        >
          {processing ? (
            <Loading />
          ) : (
            <>
              <SecondaryButton
                type="button"
                onClick={(e) => {
                  handleReset();
                }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit">Save Account Details</PrimaryButton>
            </>
          )}
        </FormFooter>
      )}
    </form>
  );
};

export { EditUserAccountDetails };
